import { Button, CloseModalButton } from '@library/components/buttons';
import { classNames, convertCentsToDollars } from '@library/utils';
import { Checkbox } from '@library/components/inputs';
import type { Deposit } from '@library/types/payments';
import { Modal } from '@library/components/modals/Modal';
import { useDepositMethodInfo } from '@bo-hooks/deposits/useDepositMethodInfo';
import { useState } from 'react';

export interface DepositsRefundConfirmationModalProps {
  deposits: Deposit[];
  onCancelModal: () => void;
  onConfirm: (deposits: Deposit[]) => void;
  error: string | undefined;
}

// TODO: This modal needs to be using our existing standards
// and replace all the duplicated code with our shared components

export const DepositsRefundConfirmationModal = ({
  deposits,
  error,
  onCancelModal,
  onConfirm
}: DepositsRefundConfirmationModalProps) => {
  const [deficiencyBypass, setDeficiencyBypass] = useState(false);
  const computedRefundAmount = deposits.reduce(
    (acc, deposit) => acc + deposit.amount,
    0
  );
  const [refunding, setRefunding] = useState(false);
  const { getDepositMethodInfo } = useDepositMethodInfo();

  const handleRefundInitiated = () => {
    onConfirm(deposits);
    setRefunding(true);
  };

  const confirmBtnText = error ? 'Retry' : 'Yes, refund deposit';
  const closeBtnText = error ? 'Cancel' : 'Close';
  const confirmBtnClasses = deficiencyBypass
    ? 'bg-roses'
    : 'bg-roses opacity-50';

  const depositMethodInfo = (deposit: Deposit) => {
    const { methodLine1, methodLine2 } = getDepositMethodInfo(deposit);

    return `${methodLine1} ${methodLine2}`;
  };

  return (
    <Modal isOpen onCancelModal={onCancelModal}>
      <div
        id="image"
        className="bg-soFresh-100 outline-hidden focus:outline-hidden relative w-full flex-col rounded-lg border border-solid border-gray-600 shadow-lg"
      >
        <div className="border-soFresh-140 flex justify-between border-b border-solid p-6">
          {!error && (
            <div className="flex h-full items-center justify-center">
              <h3 className="text-lg font-bold">Are you sure?</h3>
            </div>
          )}
          <CloseModalButton
            handleOnClick={onCancelModal}
            useDefaultStyling={false}
            btnClassName=""
            className="flex-1 text-right"
          />
        </div>

        <div className="text-soClean-110 border-soFresh-140 border-b border-solid p-6 text-sm">
          <div className="mb-4">
            {error ? (
              <>
                <p className="pb-4">
                  There was an error refunding this deposit:
                </p>
                <p>{error}</p>
              </>
            ) : (
              <>
                Are you sure you want to refund this deposit of{' '}
                <span className="font-bold">
                  {convertCentsToDollars(computedRefundAmount)}
                </span>
                ?
              </>
            )}
            {/* list of all deposits and their individual ammounts in the format of * ID <UUID> - $<Dollar Amount> */}
            <ul className="mt-5">
              {deposits.map((deposit) => (
                <li key={deposit.merchantReferenceId}>
                  {depositMethodInfo(deposit)} -{' '}
                  {convertCentsToDollars(deposit.amount)}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {!error && (
          <div className="flex">
            <span className="flex items-center justify-start p-6">
              <Checkbox
                id="refund-confirm-deficiency-bypass"
                onCheckboxChange={() => setDeficiencyBypass(!deficiencyBypass)}
                checked={deficiencyBypass}
              />
              <label
                htmlFor="refund-confirmation"
                className="text-soClean-140 max-w-60 text-xs"
              >
                By checking this box you are agreeing to bypass their account
                balance deficiency
              </label>
            </span>
            <span className="ml-auto flex h-full items-end justify-end px-6 py-2">
              <Button
                className="bg-soFresh-120 text-soClean-100 my-3 mr-2 flex justify-center rounded-lg p-3 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                type="button"
                title="DepositsDetailModalCloseButton"
                onClick={onCancelModal}
                disabled={refunding}
              >
                {closeBtnText}
              </Button>
              <Button
                className={classNames(
                  'text-soClean-100 my-3 flex justify-center rounded-lg p-3 text-sm font-semibold',
                  confirmBtnClasses
                )}
                title="DepositsDetailModalRefundButton"
                type="button"
                onClick={handleRefundInitiated}
                disabled={!deficiencyBypass}
              >
                {refunding ? (
                  <div
                    className="inline-block size-5 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  >
                    <span className="text-soClean-100 !absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                      Loading...
                    </span>
                  </div>
                ) : (
                  confirmBtnText
                )}
              </Button>
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};
